import { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../hooks";
import type { AppDispatch } from "../../store";
import { useCesiumContext } from "../cesium/CesiumContext";
import { useForm } from "react-hook-form";
import { Id, toast } from "react-toastify";
import {
  Cartesian3,
  Cartographic,
  ClockRange,
  Entity,
  JulianDate,
  Math as CesiumMath,
  Matrix4,
  Scene,
  ShadowMode,
} from "@cesium/engine";
import { csvParse } from "d3";
import { useClockManager, ClockManager } from "../clock";
import { Dialog, DialogTrigger, DialogContent, DialogTitle} from "../base/Dialog";
import { VisuallyHidden} from "react-aria";
import { isDefined, delay, cn } from "../../lib/utils";
import { parseUTCStringAsUTCDate } from "../../lib/dates";
import { getTraverseTimeRange } from "../../lib/cesium/TraverseUtility";
import { Button } from "../base/Button";
import { SimpleSelect } from "../base/SimpleSelect";
import Tooltip from "../base/Tooltip";
import InfoDiv from "../InfoDiv";
import { MapState, actions as MapActions } from "../../slices/mapState";
import { setImagerySyncEnabled } from "../../slices/mapSceneImagerySync";
import { setFPVEnabled } from "../../slices/firstPersonView";
import type QMViewer from "src/lib/cesium/QuickMapViewer";
import {
  findTerrainIntercept,
  findCamToEntityTerrainIntercept,
} from "./MapScene";
import { normalizeLongitude } from "../../lib/geo/formats/geojson";
import { UploadIcon, SunIcon } from "lucide-react";
import VisibilityPlot from "./VisibilityPlot";
import VisibilityEntityList from "./VisibilityEntityList";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { MODEL_OFFSETS } from "src/constants";
import { initializeVisibilityBar, showVisibilityBar } from "src/slices/visibilityBarState";
import { useGetLayerFeature } from "../FeaturePopover";
import { addFeatureToGeoJSON } from "src/components/hooks/useGeoJSON";
import { setGroundTrackFeature } from "../../slices/mapState";
import { isEqual } from "lodash-es";

const visibilityFillColor: { [key: string]: string } = {
  SUN: "rgb(255, 247, 166)",
  EARTH: "rgb(113, 152, 196)",
  ITEM1: "rgb(71, 214, 157)",
  ITEM2: "rgb(237, 167, 123)",
  ITEM3: "rgb(237, 135, 142)",
  SUN_EARTH_ECLIPSE_PREV: "rgb(92, 30, 30)",
  SUN_EARTH_ECLIPSE_PART_PREV: "rgb(166, 55, 55)",
  SUN_EARTH_ECLIPSE_WARN_PREV: "rgb(240, 177, 60)",
  SUN_EARTH_ECLIPSE: "rgba(92, 30, 30, 0.6)",
  SUN_EARTH_ECLIPSE_PART: "rgba(166, 55, 55, 0.6)",
  SUN_EARTH_ECLIPSE_WARN: "rgba(240, 177, 60, 0.6)",
};

type FormData = {
  startTime: JulianDate;
  stopTime: JulianDate;
};

const seconds = 1;
const minutes = seconds * 60;
const hours = minutes * 60;
const days = hours * 24;

function formatIntervalTimeRound(
  intervalSecs: number,
  timeunit: number
): [number, string] {
  const time = Math.round(intervalSecs / timeunit);
  const dTime = Math.abs(time - Math.floor(time));
  if (dTime > 0.01) {
    // approximate
    if (dTime < 0.1) {
      return [time, time.toFixed(1)];
    }
    return [time, time.toFixed(2)];
  }
  return [time, time.toFixed(0)];
}
function niceformatIntervalTimeRound(intervalSecs: number): [number, string] {
  let time, timeS, timeSec;
  if (intervalSecs >= days) {
    [time, timeS] = formatIntervalTimeRound(intervalSecs, days);
    timeSec = time * days;
    timeS = `${timeS}d`;
  } else if (intervalSecs >= hours) {
    [time, timeS] = formatIntervalTimeRound(intervalSecs, hours);
    timeSec = time * hours;
    timeS = `${timeS}hr`;
  } else if (intervalSecs >= minutes) {
    [time, timeS] = formatIntervalTimeRound(intervalSecs, minutes);
    timeSec = time * minutes;
    timeS = `${timeS}min`;
  } else {
    [time, timeS] = formatIntervalTimeRound(intervalSecs, seconds);
    timeSec = time * seconds;
    timeS = `${timeS}sec`;
  }

  return [timeSec, timeS];
}

function computeVisibilityImageHeight(nItems: number) {
  const visImgHeight =
    visibilityImageItemHeight() * nItems + visibilityImageTimeSampleBarHeight(); // px
  return visImgHeight;
}

export function visibilityImageItemHeight() {
  const visibilityItemHeight = 10; // px
  return visibilityItemHeight;
}

export function visibilityImageTimeSampleBarHeight() {
  const timeSampleBarHeight = 3; // px
  return timeSampleBarHeight;
}

function visibilityImageHeight() {
  return visImgHeight;
}

function visualizationImageBodieBarHeight() {
  return visibilityImageHeight() - visibilityImageTimeSampleBarHeight();
}

export function getTimelineWidth(viewer: QMViewer, clockManager: ClockManager) {
  const viewerElement = viewerTimeline(viewer, clockManager)?.container;
  if (viewerElement instanceof HTMLElement) {
    return viewerElement.offsetWidth;
  }
  console.error("Unable to access the offsetWidth property.");
  return undefined;
}

function tickTimeStep(
  viewer: QMViewer,
  clockManager: ClockManager,
  startTime: JulianDate,
  stopTime: JulianDate
) {
  // get time
  const deltaTime = JulianDate.secondsDifference(stopTime, startTime);
  // number of seconds between two pixels in the time bar
  return deltaTime / getTimelineWidth(viewer, clockManager)!;
}

function viewerTimeline(viewer: QMViewer, clockManager: ClockManager) {
  return viewer.timeline ?? clockManager.timeline;
}

type ViewState = {
  timeShouldAnimate: boolean;
  terrainLightingEnabled: boolean;
  terrainShadows: ShadowMode;
  currentTime: JulianDate;
  bodiesViewerEnabled: boolean | undefined;
  nadirViewerEnabled: boolean | undefined;
};

function getViewState(viewer: QMViewer, mapState: MapState): ViewState {
  const viewState: ViewState = {
    timeShouldAnimate: viewer.clock.shouldAnimate,
    terrainLightingEnabled: viewer.scene.globe.enableLighting,
    terrainShadows: viewer.terrainShadows,
    currentTime: JulianDate.clone(viewer.clock.currentTime),
    nadirViewerEnabled: mapState.showNadirViewer,
    bodiesViewerEnabled: mapState.showBodiesViewer,
  };
  return viewState;
}

function setViewState(
  viewer: QMViewer,
  viewState: ViewState,
  dispatch: AppDispatch
) {
  viewer.clock.shouldAnimate = viewState.timeShouldAnimate;
  // restore shadows
  viewer.scene.globe.enableLighting = viewState.terrainLightingEnabled;
  viewer.terrainShadows = viewState.terrainShadows;
  // restore time
  viewer.clock.currentTime = viewState.currentTime;
  if (viewState.nadirViewerEnabled) {
    dispatch(MapActions.setShowNadirViewer(true));
  }
  if (!viewState.bodiesViewerEnabled) {
    dispatch(MapActions.setShowBodiesViewer(false));
  }
}

function computeVisibilityInit(
  viewer: QMViewer,
  startTime: JulianDate,
  dispatch: AppDispatch,
  trackingViewers: [string | undefined, string][]
) {
  // make sure time is not playing
  viewer.clock.shouldAnimate = false;
  // disable shadows for faster rendering
  viewer.scene.globe.enableLighting = false;
  viewer.terrainShadows = ShadowMode.DISABLED;
  viewer.clock.currentTime = JulianDate.clone(startTime);
  // disable nadir viewer
  dispatch(MapActions.setShowNadirViewer(false));
  // show bodies viewer
  dispatch(MapActions.setShowBodiesViewer(true));
  // disable viewers imagery syncing
  dispatch(setImagerySyncEnabled(false));
  // remove imagery in bodies viewers
  if (viewer.qtsLight) {
    for (const bodyName of viewer.qtsLight.lightSources) {
      const navigatorId = body2NavigatorId(bodyName);
      const navigatorMap = viewer.getAuxViewer(navigatorId);
      if (navigatorMap) {
        navigatorMap.imageryLayers.removeAll();
      }
    }
  }
  if (trackingViewers.length > 0) {
    for (const entityId of trackingViewers) {
      const navigatorId = entity2NavigatorId(entityId);
      const navigatorMap = viewer.getAuxViewer(navigatorId);
      if (navigatorMap) {
        navigatorMap.imageryLayers.removeAll();
      }
    }
  }
}

function body2NavigatorId(bodyName: string) {
  return `${bodyName.toLowerCase()}Camera`;
}

function entity2NavigatorId(entityId: [string | undefined, string]) {
  return `${entityId.join(",")}`;
}

export function cartoPosToString(
  cartoPos: Cartographic | undefined,
  isLongitude360: boolean = true,
  precision = 5
) {
  if (!isDefined(cartoPos)) {
    return Array(3).fill("nan");
  }
  const coordinate: GeoJSON.Position = [
    CesiumMath.toDegrees(cartoPos.longitude),
    CesiumMath.toDegrees(cartoPos.latitude),
    cartoPos.height,
  ];
  const [camLon, camLat, camH] = normalizeLongitude(coordinate, isLongitude360);
  return [
    camLat.toFixed(precision),
    camLon.toFixed(precision),
    camH.toFixed(3),
  ];
}

async function waitForTilesLoaded(scene: Scene, abortSignal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    // Initial check in case tiles are already loaded
    if (scene.globe.tilesLoaded) {
      resolve();
      return;
    }

    const abortHandler = () => {
      scene.globe.tileLoadProgressEvent.removeEventListener(handler);
      abortSignal.removeEventListener("abort", abortHandler);
      reject(new Error("Operation canceled"));
    };

    const handler = (event: number) => {
      if (event === 0) {
        scene.globe.tileLoadProgressEvent.removeEventListener(handler);
        abortSignal.removeEventListener("abort", abortHandler);
        resolve();
      }
    };

    scene.globe.tileLoadProgressEvent.addEventListener(handler);
    // Listen for cancellation
    abortSignal.addEventListener("abort", abortHandler);
  });
}

async function waitForNextRender(scene: Scene) {
  return new Promise<void>((resolve) => {
    const onPostRender = () => {
      scene.postRender.removeEventListener(onPostRender);
      resolve();
    };
    scene.postRender.addEventListener(onPostRender);
  });
}

const abortController = new AbortController();
const abortSignal = abortController.signal;

const earthWidthAngle = 2.02; // width of the earth from the moon (deg)
const sunWidthAngle = 0.5; // width of the sun from the moon (deg)
const sunPartialEclipseAngle = (earthWidthAngle + sunWidthAngle) / 2.0; // angle between sun and earth for having an partial eclipse.
const sunEclipseAngle = (earthWidthAngle - sunWidthAngle) / 2.0; // min angle between sun and earth for having a full eclipse.
const sunEclipseWarningAngle = sunPartialEclipseAngle * 2.0; // min angle when an eclipse warning should be shown

type LightSourceVisibilityInfoProps = {
  updatePlotData: (newData: PlotData) => void;
  updateProgressInfo: (info: ComputationProgressInfo) => void;
  trackingViewers: [string | undefined, string][];
  openVisibilityPlot?: () => void;
};

function suggestedPlotTitle(trackingViewers: undefined | [string | undefined, string][] = undefined) {
  if (trackingViewers && trackingViewers.length > 0) {
    return `Sun, Earth and Selected Entities Visibility`;
  }
  return `Sun and Earth Visibility`;
}

let lsVisibilityInfoRetrievalCanceled: boolean;
let visImgHeight: number;
function useLightSourceVisibilityInfo({
  updatePlotData,
  updateProgressInfo,
  trackingViewers,
  openVisibilityPlot,
}: LightSourceVisibilityInfoProps) {
  const { map } = useCesiumContext();
  const mapState = useAppSelector((state) => state.mapState);
  const dispatch = useAppDispatch();
  const clockManager = useClockManager();

  const createVisibilityBarCanvas = (): [HTMLCanvasElement, CanvasRenderingContext2D | null] => {
    const canvas = document.createElement("canvas");
    let ctx: CanvasRenderingContext2D | null = null;
    const timelineWidth = getTimelineWidth(map, clockManager);
    if (isDefined(timelineWidth)) {
      canvas.width = timelineWidth;
      canvas.height = visImgHeight;
      // draw images to the canvas
      ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "gray";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // timeline bar
        ctx.fillStyle = "white";
        ctx.fillRect(
          0,
          visualizationImageBodieBarHeight(),
          canvas.width,
          visibilityImageTimeSampleBarHeight()
        );
      }
    }
    return [canvas, ctx];
  }

  const initEclipseInfo = async (
    startTime: JulianDate,
    stopTime: JulianDate,
    tickStep: number,
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D | null,
    cameraPos?: Cartesian3, // static camera position
    entity?: Entity,  // in case defined, its position will be used for finding the angular separation
  ): Promise<[JulianDate[], string[], (number | null)[]]> => {
    const [UTCEclipseList, sunEarthAngleList] =
      await getBodiesAngularSeparation(
        map,
        "SUN",
        "EARTH",
        startTime,
        stopTime,
        tickStep,
        sunEclipseWarningAngle,
        cameraPos,
        entity,
      );
    const UTCEclipseListS: string[] = []; // need for plotting
    if (UTCEclipseList.length > 0) {
      // show eclipse info
      let sunEarthAngle: number | null;
      let UTCTime: JulianDate;
      for (let i = 0; i < UTCEclipseList.length; i++) {
        sunEarthAngle = sunEarthAngleList[i];
        UTCTime = UTCEclipseList[i];
        if (sunEarthAngle) {
          if (ctx) {
            if (sunEarthAngle <= sunEclipseAngle) {
              // full eclipse
              ctx.fillStyle = visibilityFillColor["SUN_EARTH_ECLIPSE_PREV"];
            } else if (sunEarthAngle <= sunPartialEclipseAngle) {
              // partial eclipse
              ctx.fillStyle =
                visibilityFillColor["SUN_EARTH_ECLIPSE_PART_PREV"];
            } else {
              // warning eclipse
              ctx.fillStyle =
                visibilityFillColor["SUN_EARTH_ECLIPSE_WARN_PREV"];
            }
            const elapsedTime = JulianDate.secondsDifference(
              UTCTime,
              startTime
            );
            const previewX = Math.round(elapsedTime / tickStep);
            ctx.fillRect(previewX, 0, 1, visualizationImageBodieBarHeight());
          }
          const imgUrl = canvas.toDataURL();
          dispatch(showVisibilityBar(imgUrl));
        }
        UTCEclipseListS.push(JulianDate.toIso8601(UTCTime, 3));
      }
    }
    return [UTCEclipseList, UTCEclipseListS, sunEarthAngleList];
  }

  const updateEntityVisibilityImage = (
    ctx: CanvasRenderingContext2D,
    isVisible: boolean,
    colorEntryName: string,
    entityId: number,
    previewX: number,
    pxStep: number,
    bodyImgHeightPx: number
  ) => {
    if (isVisible) {
      // visible
      ctx.fillStyle = visibilityFillColor[colorEntryName];
      if (!isDefined(ctx.fillStyle)) {
        ctx.fillStyle = "white";
      }
    } else {
      // not visible
      ctx.fillStyle = "black";
    }
    // nearest neighboroud interpolation
    ctx.fillRect(
      previewX - Math.floor(pxStep / 2),
      entityId * bodyImgHeightPx,
      pxStep,
      bodyImgHeightPx
    );
  }

  const updateTimeSamplingVisibilityImage = (
    ctx: CanvasRenderingContext2D,
    previewX: number,
  ) => {
    // time sampling info
    ctx.fillStyle = "rgb(102, 0, 0)";
    ctx.fillRect(
      previewX,
      visualizationImageBodieBarHeight(),
      1,
      visibilityImageTimeSampleBarHeight()
    );
  }

  const updateVisibilityImageEclipseInfo = (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D | null,
    stepInterval: number,
    eclipseTimeIdx: number,
    startTime: JulianDate,
    refTime: JulianDate,
    UTCEclipseList: JulianDate[],
    sunEarthAngleList: (number | null)[],
    previewX: number,
    pxStep: number,
    tickStep: number,
  ):number => {
    const nextTime = new JulianDate();
      JulianDate.addSeconds(refTime, stepInterval, nextTime);
      let newEclipseTimeIdx: number;
      for (
        newEclipseTimeIdx = eclipseTimeIdx;
        newEclipseTimeIdx < UTCEclipseList.length &&
        JulianDate.compare(UTCEclipseList[newEclipseTimeIdx], nextTime) < 0;
        newEclipseTimeIdx++
      );
      if (newEclipseTimeIdx !== eclipseTimeIdx) {
        // update eclipse info
        let sunEarthAngle: number | null;
        let UTCTime: JulianDate;
        for (; eclipseTimeIdx < newEclipseTimeIdx; eclipseTimeIdx++) {
          sunEarthAngle = sunEarthAngleList[eclipseTimeIdx];
          if (sunEarthAngle) {
            UTCTime = UTCEclipseList[eclipseTimeIdx];
            if (ctx) {
              if (sunEarthAngle <= sunEclipseAngle) {
                // full eclipse
                ctx.fillStyle = visibilityFillColor["SUN_EARTH_ECLIPSE"];
              } else if (sunEarthAngle <= sunPartialEclipseAngle) {
                // partial eclipse
                ctx.fillStyle = visibilityFillColor["SUN_EARTH_ECLIPSE_PART"];
              } else {
                // warning eclipse
                ctx.fillStyle = visibilityFillColor["SUN_EARTH_ECLIPSE_WARN"];
              }
            }
            const eclipseElapsedTime = JulianDate.secondsDifference(
              UTCTime,
              startTime
            );
            const eclipsePreviewX = Math.round(eclipseElapsedTime / tickStep);
            if (eclipsePreviewX + 1 > previewX + Math.floor(pxStep / 2) - 1) {
              // avoid to update part of the image that hasn't been updated yet
              break;
            }
            if (ctx)
              ctx.fillRect(
                eclipsePreviewX,
                0,
                1,
                visualizationImageBodieBarHeight()
              );
          }
        }
        const imgUrl = canvas.toDataURL();
        dispatch(showVisibilityBar(imgUrl));
      }
      return eclipseTimeIdx;
  }

  const computeVisibilityInfo = async (
    stepInterval: number,
    startTime: JulianDate,
    stopTime: JulianDate): Promise<Blob | null> => {
  
    const progressInfo: ComputationProgressInfo = {
      timeStep: stepInterval,
      numSteps: 0,
      currentStep: 0,
      execTime: 0,
    };

    lsVisibilityInfoRetrievalCanceled = false;
    if (!map.qtsLight) return null;

    // store current view state
    const viewStateBak = getViewState(map, mapState);
    // initialize visibility computation
    computeVisibilityInit(map, startTime, dispatch, trackingViewers);
    // locking canvas size
    map.setViewportSize(map.canvas.clientWidth, map.canvas.clientHeight);
    
    // create the list of entities of interest
    const entitiesVisList: {
      [key: string]: { navigatorId: string; body: boolean };
    } = {};

    for (const bodyName of map.qtsLight.lightSources) {
      entitiesVisList[bodyName] = {
        navigatorId: body2NavigatorId(bodyName),
        body: true,
      };
    }
    for (const entityId of trackingViewers) {
      entitiesVisList[entityId.join(",")] = {
        navigatorId: entity2NavigatorId(entityId),
        body: false,
      };
    }
    const nEntities = Object.keys(entitiesVisList).length;
    // settings
    visImgHeight = computeVisibilityImageHeight(nEntities); // px

    const startExecTime = Date.now();
    // create a canvas for timeline image
    const [canvas, ctx] = createVisibilityBarCanvas();

    // build csv file with following data
    // UTC, CLLH, S_VIS, C2S_LLH, S_ELE, S_AZM, E_VIS, C2E_LLH, E_ELE, E_AZM
    const csvDataRows = [];
    const csvHeader = ["UTC"];
    csvHeader.push(`CAMERA_LAT`);
    csvHeader.push(`CAMERA_LON`);
    csvHeader.push(`CAMERA_HEIGHT`);
    for (const bodyName of map.qtsLight.lightSources) {
      csvHeader.push(`${bodyName}_VIS`);
      csvHeader.push(`CAMERA_TO_${bodyName}_INT_LAT`);
      csvHeader.push(`CAMERA_TO_${bodyName}_INT_LON`);
      csvHeader.push(`CAMERA_TO_${bodyName}_INT_HEIGHT`);
      csvHeader.push(`${bodyName}_ELE`);
      csvHeader.push(`${bodyName}_AZMT`);
    }
    for (const entityId of trackingViewers) {
      const entityName = `ENTITY_${entityId[1].toUpperCase()}`;
      csvHeader.push(`${entityName.toUpperCase()}_VIS`);
      csvHeader.push(`CAMERA_TO_${entityName}_INT_LAT`);
      csvHeader.push(`CAMERA_TO_${entityName}_INT_LON`);
      csvHeader.push(`CAMERA_TO_${entityName}_INT_HEIGHT`);
    }
    if (
      map.qtsLight.lightSources.includes("SUN") &&
      map.qtsLight.lightSources.includes("EARTH")
    ) {
      csvHeader.push(`SUN_EARTH_ANGLE`);
    }
    csvDataRows.push(csvHeader);
    const camera = map.camera;
    const deltaTime = JulianDate.secondsDifference(stopTime, startTime);
    // number of seconds between two pixels in the time bar
    const tickStep = deltaTime / getTimelineWidth(map, clockManager)!;
    const pxStep = Math.ceil(stepInterval / tickStep);
    const bodyImgHeightPx = visibilityImageItemHeight();
    const newDate = new JulianDate();
    let nRows = 0;

    progressInfo.numSteps = Math.ceil(deltaTime / stepInterval) + 1;
    updateProgressInfo(progressInfo);

    // get bodies eclipse info
    const [UTCEclipseList, UTCEclipseListS, sunEarthAngleList] = await initEclipseInfo(
      startTime,
      stopTime,
      tickStep,
      canvas,
      ctx,
      map.camera.positionWC,
      map.trackedEntity
    );
    const eclipseData: [string[], (number | null)[]] = [UTCEclipseListS, sunEarthAngleList];

    const plotTitle = suggestedPlotTitle(trackingViewers);
    updatePlotData({
      title: plotTitle,
      csvData: csvDataRows,
      nRows,
      eclipseData,
    });
    let eclipseTimeIdx: number = 0;
    // wait a little bit to make sure the bodies viewer tiles loading has started
    // (it can take a bit of time)
    await delay(100);
    while (
      JulianDate.compare(map.clock.currentTime, stopTime) <= 0 &&
      !lsVisibilityInfoRetrievalCanceled
    ) {
      // wait terrain fully loaded
      let navigatorScene: Scene | undefined;
      for (const entityName in entitiesVisList) {
        const navigatorId = entitiesVisList[entityName].navigatorId;
        navigatorScene = map.getAuxViewer(navigatorId)?.scene;
        if (navigatorScene) {
          if (!lsVisibilityInfoRetrievalCanceled) {
            await waitForTilesLoaded(navigatorScene, abortSignal);
          }
        }
      }
      // delay for mitigating the fact that sometimes the terrain is not fully rendered
      await delay(100);

      if (nRows === 0) {
        // initialize visibility bar
        // Note: this is done here as the camera is at the right position
        const visibilityBarData = {
          startTime: startTime,
          stopTime: stopTime,
          cameraPosition: Cartesian3.clone(map.adjustCartesianInv(map.camera.positionWC)),
          trackingViewers: trackingViewers,
          trackedEntity: map.trackedEntity,
          openVisibilityPlot: openVisibilityPlot,
        };
        dispatch(initializeVisibilityBar(visibilityBarData));
      }

      const csvDataRow = [];
      // UTC TIME
      csvDataRow.push(JulianDate.toIso8601(map.clock.currentTime, 3));
      // CAMERA POS
      const cameraCartoPos = cartoPosToString(map.getCameraCartoPos());
      csvDataRow.push(...cameraCartoPos);
      const elapsedTime = JulianDate.secondsDifference(
        map.clock.currentTime,
        startTime
      );
      const previewX = Math.round(elapsedTime / tickStep);
      let entityId = 0;
      let entityCount = 0;
      for (const entityName in entitiesVisList) {
        const navigatorId = entitiesVisList[entityName].navigatorId;
        navigatorScene = map.getAuxViewer(navigatorId)?.scene;
        let interceptPoint: Cartesian3 | undefined;
        let colorEntryName: string = "";
        if (navigatorScene) {
          if (entitiesVisList[entityName].body) {
            // this is a body
            const entityPos = map.qtsLight.getBodySampledPosition(
              entityName,
              map.clock.currentTime
            );
            interceptPoint = entityPos
              ? findTerrainIntercept(
                  camera.positionWC,
                  entityPos,
                  navigatorScene
                )
              : undefined;
            colorEntryName = entityName.toUpperCase();
          } else {
            // find the lander reference location
            const entityInfo = entityName.split(",");
            const entity = map.getEntity({
              datasourceId: entityInfo[0],
              entityId: entityInfo[1],
            });
            if (!entity) return null;
            const modelUri = entity.model?.uri?.getValue(map.clock.currentTime);
            const entityOffset = MODEL_OFFSETS[modelUri] ?? Cartesian3.ZERO;
            const refPos = map.getEntityPosition(
              entity,
              map.clock.currentTime,
              entityOffset
            );
            // intercept points from camera to ref point
            interceptPoint = refPos
              ? findCamToEntityTerrainIntercept(
                  camera.positionWC,
                  refPos,
                  navigatorScene
                )
              : undefined;
            colorEntryName = `ITEM${(entityCount++ % 3) + 1}`;
          }
        }
        if (ctx) {
          updateEntityVisibilityImage(
            ctx,
            !isDefined(interceptPoint),
            colorEntryName,
            entityId,
            previewX,
            pxStep,
            bodyImgHeightPx);
        }
        // populate csv file
        // is visible
        csvDataRow.push(isDefined(interceptPoint) ? "0" : "1");
        // carto coords: lat, lon, height
        csvDataRow.push(
          ...cartoPosToString(
            map.cartesianToCartographicAdjusted(interceptPoint)
          )
        );
        if (entitiesVisList[entityName].body) {
          // elevation
          const elevationAngleRad =
            map.qtsLight.getBodyElevationAngle(entityName) ?? NaN;
          csvDataRow.push(CesiumMath.toDegrees(elevationAngleRad).toFixed(2));
          // azimuth
          const azimuthAngleRad =
            map.qtsLight.getBodyAzimuthAngle(entityName) ?? NaN;
          csvDataRow.push(CesiumMath.toDegrees(azimuthAngleRad).toFixed(2));
        }

        entityId += 1;
      }
      if (ctx) {
        // time sampling info
        updateTimeSamplingVisibilityImage(ctx, previewX);
      }
      if (csvHeader.includes("SUN_EARTH_ANGLE")) {
        const sunEarthAngleRad =
          map.qtsLight.getBodiesSampledAngleFromCamera(
            "SUN",
            "EARTH",
            map.clock.currentTime
          ) ?? NaN;
        const sunEarthAngle = CesiumMath.toDegrees(sunEarthAngleRad);
        csvDataRow.push(sunEarthAngle.toFixed(2));
      }

      // check whether we have overriden eclipse info
      eclipseTimeIdx = updateVisibilityImageEclipseInfo(
        canvas,
        ctx,
        stepInterval,
        eclipseTimeIdx,
        startTime,
        map.clock.currentTime,
        UTCEclipseList,
        sunEarthAngleList,
        previewX,
        pxStep,
        tickStep,
      );

      csvDataRows.push(csvDataRow);
      nRows += 1;

      // update preview
      const imgUrl = canvas.toDataURL();
      dispatch(showVisibilityBar(imgUrl));

      // update plots
      updatePlotData({
        title: plotTitle,
        csvData: csvDataRows,
        nRows,
        eclipseData,
      });

      // STOP condition
      if (JulianDate.compare(map.clock.currentTime, stopTime) === 0) {
        break;
      }

      // advance time
      // Note: avoid to set the current time to a value greater than the clock stop time
      // as that might cause problems with processing the current scene ligth sources
      JulianDate.addSeconds(map.clock.currentTime, stepInterval, newDate);
      if (JulianDate.compare(newDate, stopTime) < 0) {
        JulianDate.clone(newDate, map.clock.currentTime);
      } else {
        JulianDate.clone(stopTime, map.clock.currentTime);
      }
      // wait for the scene to start rendering
      for (const entityName in entitiesVisList) {
        const navigatorId = entitiesVisList[entityName].navigatorId;
        navigatorScene = map.getAuxViewer(navigatorId)?.scene;
        if (navigatorScene) {
          if (!lsVisibilityInfoRetrievalCanceled) {
            navigatorScene.requestRender();
            await waitForNextRender(navigatorScene);
          }
        }
      }

      if (TOAST_ID) {
        let progress = (tickStep / deltaTime) * (previewX + 1);
        if (progress === 1.0) {
          // prevent toast dialog closure
          progress = 0.99; 
        }
        toast.update(TOAST_ID, {
          progress: progress,
        });
      }
      updateProgressInfo({
        ...progressInfo,
        currentStep: nRows,
      });
    }

    const endExecTime = Date.now();
    updateProgressInfo({
      ...progressInfo,
      currentStep: -1, // hide current step
      execTime: (endExecTime - startExecTime) / 1000, // secs
    });

    // restore initial view
    setViewState(map, viewStateBak, dispatch);
    // re-enable viewers imagery syncing
    dispatch(setImagerySyncEnabled(true));

    // return csv file
    const csvContent = csvDataRows.map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], {
      type: "data:text/csv;charset=utf-8",
    });
    const csvUrl = URL.createObjectURL(blob);
    updateProgressInfo({
      ...progressInfo,
      csvUrl: csvUrl,
    });
    return blob;
  }

  const loadVisibilityInfo = async (
    stepInterval: number,
    startTime: JulianDate,
    stopTime: JulianDate,
    csvData: string[][]): Promise<Blob | null> => {
      
    const progressInfo: ComputationProgressInfo = {
      timeStep: stepInterval,
      numSteps: 0,
      currentStep: 0,
      execTime: 0,
    };

    lsVisibilityInfoRetrievalCanceled = false;
    
    const csvHeader = csvData[0];
    const entitiesList = csvHeader
      .filter(str => str.endsWith("_VIS"))
      .map(str => str.slice(0, -4));
    const nEntities = entitiesList.length;
    // settings
    visImgHeight = computeVisibilityImageHeight(nEntities); // px

    // create a canvas for timeline image
    const [canvas, ctx] = createVisibilityBarCanvas();

    const deltaTime = JulianDate.secondsDifference(stopTime, startTime);
    // number of seconds between two pixels in the time bar
    const tickStep = deltaTime / getTimelineWidth(map, clockManager)!;
    const pxStep = Math.ceil(stepInterval / tickStep);
    const bodyImgHeightPx = visibilityImageItemHeight();
    let nRows = 0;

    progressInfo.numSteps = Math.ceil(deltaTime / stepInterval) + 1;
    updateProgressInfo(progressInfo);

    // csvHeader -> UTC, LAT, LON, HEIGHT, ...
    const firstDataRow = csvData[1];
    const cameraPos = Cartesian3.fromDegrees(Number(firstDataRow[2]), Number(firstDataRow[1]), Number(firstDataRow[3]), map.scene.ellipsoid);
    if (Matrix4.equals(map.camera.transform, Matrix4.IDENTITY)) {
      // update camera position
      Cartesian3.clone(cameraPos, map.camera.position);
    }
    // initialize visibility bar
    const visibilityBarData = {
      startTime: startTime,
      stopTime: stopTime,
      cameraPosition: cameraPos,
      trackingViewers: [],
      trackedEntity: map.trackedEntity,
      openVisibilityPlot: openVisibilityPlot,
    };
    dispatch(initializeVisibilityBar(visibilityBarData));

    // get bodies eclipse info
    const [UTCEclipseList, UTCEclipseListS, sunEarthAngleList] = await initEclipseInfo(
      startTime,
      stopTime,
      tickStep,
      canvas,
      ctx,
      cameraPos,
      map.trackedEntity,
    );
    const eclipseData: [string[], (number | null)[]] = [UTCEclipseListS, sunEarthAngleList];

    const plotTitle = suggestedPlotTitle();
    updatePlotData({
      title: plotTitle,
      csvData: csvData,
      nRows,
      eclipseData,
    });
    let eclipseTimeIdx: number = 0;
    while (
      nRows < csvData.length - 1 &&
      !lsVisibilityInfoRetrievalCanceled
    ) {
      const csvDataRow = csvData[nRows + 1];
      const entryUtc = JulianDate.fromIso8601(csvDataRow[0]);
      const elapsedTime = JulianDate.secondsDifference(
        entryUtc,
        startTime
      );
      const previewX = Math.round(elapsedTime / tickStep);
      let entityId = 0;
      let entityCount = 0;
      for (const entityName of entitiesList) {
        const visIdx = csvHeader.indexOf(`${entityName}_VIS`);
        const interceptPoint = !isNaN(Number(csvDataRow[visIdx + 1])) ?
            Cartesian3.fromDegrees(Number(csvDataRow[visIdx + 2]), Number(csvDataRow[visIdx + 1]), Number(csvDataRow[visIdx + 3]), map.scene.ellipsoid)
            : undefined;
        const colorEntryName = Object.keys(visibilityFillColor).includes(entityName.toUpperCase()) ? 
          entityName.toUpperCase() : `ITEM${(entityCount++ % 3) + 1}`;
        
        if (ctx) {
          updateEntityVisibilityImage(
            ctx,
            !isDefined(interceptPoint),
            colorEntryName,
            entityId,
            previewX,
            pxStep,
            bodyImgHeightPx);
        }
        entityId += 1;
      }
      if (ctx) {
        // time sampling info
        updateTimeSamplingVisibilityImage(ctx, previewX);
      }
      
      // check whether we have overriden eclipse info
      eclipseTimeIdx = updateVisibilityImageEclipseInfo(
        canvas,
        ctx,
        stepInterval,
        eclipseTimeIdx,
        startTime,
        entryUtc,
        UTCEclipseList,
        sunEarthAngleList,
        previewX,
        pxStep,
        tickStep,
      );
      
      nRows += 1;

      // update preview
      const imgUrl = canvas.toDataURL();
      dispatch(showVisibilityBar(imgUrl));

      // update plots
      updatePlotData({
        title: plotTitle,
        csvData: csvData,
        nRows,
        eclipseData,
      });

      if (TOAST_ID) {
        let progress = (tickStep / deltaTime) * (previewX + 1);
        if (progress === 1.0) {
          // prevent toast dialog closure
          progress = 0.99; 
        }
        toast.update(TOAST_ID, {
          progress: progress,
        });
      }
      updateProgressInfo({
        ...progressInfo,
        currentStep: nRows,
      });
    }

    updateProgressInfo({
      ...progressInfo,
      currentStep: -1, // hide current step
    });

    // return csv file
    const csvContent = csvData.map((e) => e.join(",")).join("\n");
    const blob = new Blob([csvContent], {
      type: "data:text/csv;charset=utf-8",
    });
    const csvUrl = URL.createObjectURL(blob);
    updateProgressInfo({
      ...progressInfo,
      csvUrl: csvUrl,
    });
    return blob;
  }

  return async (
    stepInterval: number,
    startTime: JulianDate,
    stopTime: JulianDate,
    csvData?: string[][],
  ): Promise<Blob | null> => {
    let blob: Blob | null;
    if (!isDefined(csvData)) {
      // compute
      blob = await computeVisibilityInfo(stepInterval, startTime, stopTime);
    } else {
      // load
      blob = await loadVisibilityInfo(stepInterval, startTime, stopTime, csvData);
    }
    return blob;
  };
}

function roundJulianDate(jd: JulianDate, precision: number = 3) {
  const dateStr = JulianDate.toIso8601(jd, precision);
  return JulianDate.fromIso8601(dateStr).clone(new RoundedJulianDate());
}
// return empty arrays if the angle between bodies is never lower than the angle threashold provided
// otherwise return the list of utc values and valid angles when lower than the threashold, null otherwise
// NOTE: this is needed for properly plotting discontinous values
async function getBodiesAngularSeparation(
  viewer: QMViewer,
  body1: string,
  body2: string,
  startTime: JulianDate,
  stopTime: JulianDate,
  step: number,
  angleTh: number,
  cameraPos?: Cartesian3,
  entity?: Entity,
): Promise<[JulianDate[], (number | null)[]]> {
  if (!viewer.qtsLight) return [[], []];
  let UTCList: JulianDate[] = [];
  let angleList: (number | null)[] = [];

  if (!isDefined(angleTh)) {
    // just accept all values
    angleTh = 500;
  }

  // draw eclipse info
  let eclipseIntervalFound = false;
  const currTime = new JulianDate();
  JulianDate.clone(startTime, currTime);
  let bodiesAngle: number;
  while (JulianDate.compare(currTime, stopTime) <= 0) {
    const currCameraPos = entity?.position?.getValue(currTime) ?? cameraPos;
    // check if an eclipse is happening
    const bodiesAngleRadians =
      await viewer.qtsLight.getBodiesSampledAngleFromCameraAsync(
        body1,
        body2,
        currTime,
        currCameraPos
      );
    if (!isDefined(bodiesAngleRadians)) {
      return [[], []];
    }

    bodiesAngle = CesiumMath.toDegrees(bodiesAngleRadians);
    if (bodiesAngle <= angleTh) {
      angleList.push(bodiesAngle);
      eclipseIntervalFound = true;
    } else {
      angleList.push(null);
    }
    const time = new JulianDate();
    JulianDate.clone(currTime, time);
    UTCList.push(time);

    // advance time
    JulianDate.addSeconds(currTime, step, currTime);
  }
  if (!eclipseIntervalFound) {
    // no eclipse found
    UTCList = [];
    angleList = [];
  }
  return [UTCList, angleList];
}

function setUserInteractionEnabled(viewer: QMViewer, yes: boolean) {
  viewer.setUserInteractionEnabled(yes);
  // even better disable all mouse events in container except for toast that is rendered outside
  // this is good because it prevents timeline and layer tree interactions.
  const quickmapContainer = document.getElementById("QuickMap");
  if (quickmapContainer) {
    quickmapContainer.style.pointerEvents = yes ? "auto" : "none";
  }
}

function onVisibilityComputationStart(viewer: QMViewer) {
  setUserInteractionEnabled(viewer, false);
}

function onVisibilityComputationFinished(viewer: QMViewer) {
  setUserInteractionEnabled(viewer, true);
  // reset view
  viewer.setViewportSize();
}

type PlotData = {
  title: string;
  csvData: string[][];
  nRows: number;
  eclipseData: [string[], (number | null)[]];
};

type ComputationProgressInfo = {
  timeStep: number;
  numSteps: number;
  currentStep: number;
  execTime: number;
  csvUrl?: string;
};

function dateToJulianDate(date: any) {
  if (date instanceof JulianDate) return date;
  if (!isDefined(date) || (date instanceof Date && isNaN(date.getTime())))
    return;
  return JulianDate.fromDate(
    date instanceof Date ? date : parseUTCStringAsUTCDate(date)
  );
}
let TOAST_ID: Id;

class RoundedJulianDate extends JulianDate {
  toString() {
    return JulianDate.toIso8601(this, 3);
  }
}

const BUTTON_VARIANT = {
  secondary: "Secondary Button",
} as const;

type VariantTypes = keyof typeof BUTTON_VARIANT;

type VisibilityInfoProps = {
  variant?: VariantTypes;
  size?: "sm";
  isDisabled?: boolean;
};

export default function VisibilityInfo({ variant, size, isDisabled = false }: VisibilityInfoProps) {
  const buttonProps = {
    ...(variant && { variant }),
    ...(size && { size }),
  };

  const { map } = useCesiumContext();
  const clockManager = useClockManager();
  const clock = clockManager.clock;
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUpdateFinished, setIsUpdateFinished] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [stepInterval, setStepInterval] = useState<number>();
  const [plotData, setPlotData] = useState<PlotData | null>();
  const [progressInfo, setProgressInfo] = useState<ComputationProgressInfo>({
    timeStep: 0,
    numSteps: 0,
    currentStep: 0,
    execTime: 0,
  });
  const mapState = useAppSelector((state) => state.mapState);
  const trackingViewers = mapState.trackingViewers;
  const [visibilityTrackingViewers, setVisibilityTrackingViewers] =
    useState<[string | undefined, string][]>(trackingViewers);

  const FPVEnabled = useAppSelector(
    (state) => state.firstPersonView.FPVEnabled
  );

  const visibilityInfoVisible = useAppSelector(
    (state) => state.visibilityBarState.visible
  );

  const dispatch = useAppDispatch();
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    trigger,
  } = useForm<FormData>({ mode: "all", reValidateMode: "onChange" });

  const startTimeJD = watch("startTime");
  const stopTimeJD = watch("stopTime");

  const updatePlotData = (data: PlotData) => {
    setPlotData(data);
  };
  const updateProgressInfo = (info: ComputationProgressInfo) => {
    setProgressInfo(info);
  };

  const openPlotDialog = () => {
    // trick for accessing the latest plotData when invoked
    // (this is called by another component)
    setPlotData((currentPlotData) => {
      if (currentPlotData) {
        setIsDialogOpen(true);
      }
      return currentPlotData; // Preserve the current state
    });
  };

  useEffect(() => {
    if (!visibilityInfoVisible && plotData) {
      // discard data
      setPlotData(null);
    }
  }, [visibilityInfoVisible, plotData]);

  const getVisibilityInfo = useLightSourceVisibilityInfo({
    updatePlotData,
    updateProgressInfo,
    trackingViewers: visibilityTrackingViewers,
    openVisibilityPlot: openPlotDialog,
  });
  const onSubmit = async () => {
    if (isProcessing) return;

    if (!isDefined(stepInterval)) {
      toast.error("Invalid step interval.\nPlease select a valid time step.");
      return;
    }
    setIsUpdateFinished(false);
    setIsProcessing(true);
    const FPVEnabledBak = FPVEnabled;
    TOAST_ID = toast("Computing visibility...", {
      autoClose: false,
      draggable: false,
      closeButton: true,
      closeOnClick: false,
      onClose: () => {
        lsVisibilityInfoRetrievalCanceled = true;
        abortController.abort();
      },
      progress: 0,
    });
    try {
      if (!isDefined(startTimeJD) || !isDefined(stopTimeJD)) return;
      // constraint time
      clockManager.onTimeChange(
        JulianDate.toDate(map.clock.currentTime),
        JulianDate.toDate(startTimeJD),
        JulianDate.toDate(stopTimeJD)
      );
      clock.clockRange = ClockRange.LOOP_STOP;
      clock.shouldAnimate = false;

      // clear old data
      setPlotData(null);

      if (map.trackedEntity && !FPVEnabled) {
        // disable collision detection to avoid the camera positiong is adjusted while computing visibility info
        dispatch(setFPVEnabled(true));
      }

      onVisibilityComputationStart(map);
      
      const dataBlob = await getVisibilityInfo(
        Number(stepInterval),
        startTimeJD,
        stopTimeJD
      );
      if (lsVisibilityInfoRetrievalCanceled) return;
      if (isDefined(dataBlob)) {
        // saveBlobAs(dataBlob, "data.csv");
        toast.update(TOAST_ID, {
          type: "success",
          render: "Visibility analysis completed!",
        });
        setIsUpdateFinished(true);
      } else {
        toast.update(TOAST_ID, {
          type: "error",
          render: "Unable to compute visibility",
        });
      }
    } catch (error) {
      toast.update(TOAST_ID, {
        type: "error",
        render: "Error computing visibility",
      });
    } finally {
      onVisibilityComputationFinished(map);
      toast.dismiss(TOAST_ID);
      setIsProcessing(false);
      setIsUpdateFinished(true);
      if (map.trackedEntity && !FPVEnabledBak) {
        dispatch(setFPVEnabled(false));
      }
    }
  };

  const parseVisibilityInfoFile = async (file: File): Promise<string[][]> => {
    return new Promise ((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const csvContent = reader.result as string;
          const parsedData = csvParse(csvContent);
          const headers = parsedData.columns;
          const rows = parsedData.map((row) => headers.map((header) => row[header] ?? ""));
          const result = [headers, ...rows]; 
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error("File reading failed."));
      };

      reader.readAsText(file);
    });
  };

  const cameraPosList2Feature = (coords: number[][], featureLabel?: string) => {
    if (coords.length < 2) return null;
    let type = isEqual(coords[0], coords[coords.length - 1]) ? "Polygon" : "LineString";
    const coordinates = coords;

    const feature = {
      type: "Feature",
      geometry: {
        type,
        coordinates,
      },
      properties: {
        label: featureLabel
      },
    };
    
    const layerId = "@@user-defined";
    const featureId = addFeatureToGeoJSON(layerId, feature as GeoJSON.Feature);
    return [layerId, featureId];
  }

  const maybeRestoreFeature = (csvData: string[][], featureLabel?: string) => {
    if (csvData.length < 3) return null; // header + at least 2 camera positions
    // check camera pos
    const csvHeader = csvData[0];
    const cameraLatIdx = csvHeader.indexOf("CAMERA_LAT");
    const cameraLonIdx = csvHeader.indexOf("CAMERA_LON");
    const pos1 = [Number(csvData[1][cameraLonIdx]), Number(csvData[1][cameraLatIdx])];
    const pos2 = [Number(csvData[2][cameraLonIdx]), Number(csvData[2][cameraLatIdx])];
    if (isEqual(pos1, pos2)) return null;
    const utcIdx = csvHeader.indexOf("UTC");
    // discard header
    csvData = csvData.slice(1);
    const startUtc = JulianDate.fromIso8601(csvData[0][utcIdx]);
    // extract camera traverse coords
    const coords = csvData.map(row => [Number(row[cameraLonIdx]), Number(row[cameraLatIdx])]);
    const elapsed = csvData.map(row => JulianDate.secondsDifference(JulianDate.fromIso8601(row[utcIdx]), startUtc));
    const feature = cameraPosList2Feature(coords, featureLabel);
    return feature ? [...feature, elapsed] : feature;
  }

  const onLoadVisibilityInfo = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isProcessing) return;

    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;
    if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith(".csv")) {
      console.log("No CSV file");
      return;
    }
    setIsUpdateFinished(false);
    setIsProcessing(true);
    TOAST_ID = toast("Loading visibility...", {
      autoClose: false,
      draggable: false,
      closeButton: true,
      closeOnClick: false,
      onClose: () => {
        lsVisibilityInfoRetrievalCanceled = true;
        abortController.abort();
      },
      progress: 0,
    });
    try {
      // parse file
      let csvData = await parseVisibilityInfoFile(selectedFile);
      const nRows = csvData.length - 1;
      if (nRows < 2) {
        console.log("Invalid Visibility Info CSV file");
        return;
      }
      const csvHeader = csvData[0];
      const trackedItemStartColIdx = csvHeader.findIndex(str => str.startsWith("ENTITY_"));
      if (trackedItemStartColIdx >= 0) {
        // DISCARD TRACKED ENTITY VISIBILITY INFO
        // AS WE DO NOT HAVE ENOUGH INFO FOR RESTORING THEM
        console.log("Discarding tracked entity info...");
        csvData = csvData.map(row => row.slice(0, trackedItemStartColIdx));
      }
      const UTCTimeIdx = csvHeader.indexOf("UTC");
      if (UTCTimeIdx === -1) {
        console.log("Invalid Visibility Info CSV file");
        return;
      }
      const startTime = JulianDate.fromIso8601(csvData[1][UTCTimeIdx]);
      const stopTime = JulianDate.fromIso8601(csvData[nRows][UTCTimeIdx]);
      const secondTime = JulianDate.fromIso8601(csvData[2][UTCTimeIdx]);
      if (!isDefined(startTime) || !isDefined(stopTime) || !isDefined(secondTime))  {
        console.log("Invalid Visibility Info CSV file");
        return;
      }
      // constraint time
      clockManager.onTimeChange(
        JulianDate.toDate(startTime),
        JulianDate.toDate(startTime),
        JulianDate.toDate(stopTime)
      );
      clock.clockRange = ClockRange.LOOP_STOP;
      clock.shouldAnimate = false;

      // restore traverse if needed
      const feature = maybeRestoreFeature(csvData, `Traverse (${selectedFile.name})`);
      if (feature) {
        // stop tracking current item
        if (map.trackedEntity) map.trackedEntity = undefined;
        dispatch(setGroundTrackFeature(feature));

        // Wait for `trackedEntity` to be updated
        const waitForTrackedEntityUpdate = () =>
          new Promise<void>((resolve) => {
            const interval = setInterval(() => {
              if (map.trackedEntity) {
                clearInterval(interval);
                resolve();
              }
            }, 50); // Poll every 50ms
          });

        await waitForTrackedEntityUpdate();
      }
      
      // clear old data
      setPlotData(null);

      const timeStep = JulianDate.secondsDifference(secondTime, startTime);
      const dataBlob = await getVisibilityInfo(
        timeStep,
        startTime,
        stopTime,
        csvData
      );
      if (lsVisibilityInfoRetrievalCanceled) return;
      if (isDefined(dataBlob)) {
        toast.update(TOAST_ID, {
          type: "success",
          render: "Visibility data loaded!",
        });
      } else {
        toast.update(TOAST_ID, {
          type: "error",
          render: "Unable to load visibility",
        });
      }
    } catch(error) {
      toast.update(TOAST_ID, {
        type: "error",
        render: "Error loading visibility",
      });
    } finally {
      toast.dismiss(TOAST_ID);
      setIsProcessing(false);
      setIsUpdateFinished(true);
    }
  };

  function getInitialStartTime() {
    return clock.clockRange !== ClockRange.UNBOUNDED &&
      isDefined(clock.startTime)
      ? clock.startTime
      : isDefined(clock.currentTime)
      ? clock.currentTime
      : undefined;
  }
  function getInitialStopTime() {
    return clock.clockRange !== ClockRange.UNBOUNDED &&
      isDefined(clock.stopTime)
      ? clock.stopTime
      : isDefined(clock.currentTime)
      ? JulianDate.addSeconds(
          clock.currentTime,
          map?.qtsLight?.solarDayDuration ?? 24 * 3600,
          new JulianDate()
        )
      : undefined;
  }

  function resetTime() {
    const dsTimes = map?.trackedEntity
      ? getTraverseTimeRange(map.trackedEntity)
      : undefined;
    const newStartTime = isDefined(dsTimes?.[0])
      ? dsTimes[0]
      : getInitialStartTime();
    const newStopTime = isDefined(dsTimes?.[1])
      ? dsTimes[1]
      : getInitialStopTime();
    reset({
      startTime: newStartTime ? roundJulianDate(newStartTime) : undefined,
      stopTime: newStopTime ? roundJulianDate(newStopTime) : undefined,
    });
    trigger(["startTime", "stopTime"]);
  }

  const groundTrackingFeature = useAppSelector(
    (state) => state.mapState.groundTrackFeature
  );
  const feature = useGetLayerFeature(
    groundTrackingFeature?.[0],
    groundTrackingFeature?.[1]
  );
  let isTraverse = false;
  let traverseLabel;
  if (isDefined(map?.trackedEntity?.position)) {
    isTraverse = true;
    const labelProperty = Object.keys(feature?.properties ?? []).find((p) =>
      ["name", "title", "label", "id"].includes(p.toLowerCase()));
    traverseLabel = labelProperty ? feature?.properties?.[labelProperty] : undefined;
  }

  function removeVisibilityTrackingViewer(
    entityId: [string | undefined, string]
  ) {
    setVisibilityTrackingViewers((prevViewers) =>
      prevViewers.filter(
        (viewer) => !(viewer[0] === entityId[0] && viewer[1] === entityId[1])
      )
    );
  }

  function onOpenDialogChange(open: boolean) {
    if (open === isDialogOpen) return;
    if (open) {
      resetTime();
      setVisibilityTrackingViewers(trackingViewers);
    }
    setIsDialogOpen(open);
  }

  const timeStepOptions = useMemo(() => {
    trigger(["startTime", "stopTime"]);
    if (
      !isDefined(startTimeJD) ||
      !isDefined(stopTimeJD) ||
      JulianDate.secondsDifference(stopTimeJD, startTimeJD) <= 0
    )
      return [];
    const defDeltaTimeArray = [0.5, 1, 2, 4, 8, 16, 24]; // hr
    // 5 sec, 10 sec, 30 sec, 1 min, 10 min
    defDeltaTimeArray.push(0.0014, 0.0028, 0.0083, 0.017, 0.17);

    const stepTimeArray = [];
    let tickTime = tickTimeStep(map, clockManager, startTimeJD, stopTimeJD);
    if (tickTime <= 0.5) tickTime = 1; // at least every second
    stepTimeArray.push(tickTime);
    for (let i = 0; i < defDeltaTimeArray.length; i++) {
      stepTimeArray.push(defDeltaTimeArray[i] * 3600);
    }
    stepTimeArray.sort((a, b) => a - b);
    const deltaTime = JulianDate.secondsDifference(stopTimeJD, startTimeJD);
    const newTimeStepOptions = [];
    let prevTimeStep;
    for (let i = 0; i < stepTimeArray.length; i++) {
      let timeStep = stepTimeArray[i];
      if (timeStep >= tickTime) {
        // round time step
        const [timeStepR, timeStepS] = niceformatIntervalTimeRound(timeStep);
        timeStep = timeStepR;
        const nSteps = Math.ceil(deltaTime / timeStep) + 1;
        if (
          timeStepR > 0 &&
          nSteps >= 3 && // at least 3 points
          timeStep <= days && // avoid times intervals bigger than 1 day
          timeStep !== prevTimeStep
        ) {
          // avoid to add two identical time steps
          newTimeStepOptions.push({
            label: `${nSteps} [${timeStepS}]`,
            value: timeStep.toString(),
          });
        }
        prevTimeStep = timeStep;
      }
    }
    return newTimeStepOptions;
  }, [startTimeJD, stopTimeJD]);

  useEffect(() => {
    if (timeStepOptions.length > 0) {
      if (
        !isDefined(stepInterval) ||
        !timeStepOptions.find((item) => parseFloat(item.value) === stepInterval)
      ) {
        // update time step
        setStepInterval(
          parseFloat(timeStepOptions[timeStepOptions.length - 1].value)
        );
      }
    }
  }, [timeStepOptions, stepInterval]);

  let tooltipContent = "Compute visibility info.";
  if (!isDefined(map)) {
    tooltipContent = "Only availabe in 3D mode.";
  }

  const VisibilityForm = (
    <div className="min-w-[400px]">
      <h3>Visibility Analysis</h3>
      {map?.trackedEntity && (
        <div className="mt-1 space-y-2 text-xs roboto-font">
          {`Along Traverse${traverseLabel ? `: ${traverseLabel}` : ''}`}
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="my-2">
          <div className="flex flex-col text-center my-4 mx-8 w-[250px] space-y-1">
            <div className="text-left mx-auto flex flex-col w-full">
              <label className="label">Start Time</label>
              <input
                {...register("startTime", {
                  setValueAs: (val): JulianDate | undefined => {
                    if (val instanceof JulianDate) return val;
                    return dateToJulianDate(parseUTCStringAsUTCDate(val));
                  },
                  validate: {
                    isValidDate: (value: JulianDate | undefined) => {
                      if (isDefined(value)) {
                        const date = JulianDate.toDate(value);
                        if (!(date instanceof Date))
                          return "Invalid date string please use ISO8601 format, eg. 2021-01-01T00:00:00Z";
                        if (isNaN(date.getTime()))
                          return "Invalid date string please use ISO8601 format, eg. 2021-01-01T00:00:00Z";
                        return true;
                      }
                      return false;
                    },
                    isBeforeStopTime: (value: JulianDate, formValues) => {
                      if (!value || !formValues.stopTime) return true;
                      return (
                        JulianDate.compare(value, formValues.stopTime) < 0 ||
                        "Start time must be before stop time"
                      );
                    },
                  },
                })}
                autoFocus
                type="text"
                placeholder="Enter ISO 8601 DateTime"
                onFocus={(evt) => evt.target.select()}
              />

                <InfoDiv type="error" className={cn("text-xs", { "invisible": !errors.startTime })}>
                  {errors.startTime?.message?.length
                    ? errors.startTime?.message
                    : "Invalid date string please use ISO8601 format, eg. 2021-01-01T00:00:00Z"}
                </InfoDiv>
            </div>
            <div className="text-left mx-auto flex flex-col w-full">
              <label className="label">Stop Time</label>
              <input
                {...register("stopTime", {
                  setValueAs: (val): JulianDate | undefined => {
                    if (val instanceof JulianDate) return val;
                    return dateToJulianDate(parseUTCStringAsUTCDate(val));
                  },
                  validate: {
                    isValidDate: (value: JulianDate | undefined) => {
                      if (isDefined(value)) {
                        const date = JulianDate.toDate(value);
                        if (!(date instanceof Date))
                          return "Invalid date string please use ISO8601 format, eg. 2021-01-01T00:00:00Z";
                        if (isNaN(date.getTime()))
                          return "Invalid date string please use ISO8601 format, eg. 2021-01-01T00:00:00Z";
                        return true;
                      }
                      return false;
                    },
                    isAfterStartTime: (value: JulianDate, formValues) => {
                      if (!value || !formValues.startTime) return true;
                      return (
                        JulianDate.compare(formValues.startTime, value) < 0 ||
                        "Stop time must be after start time"
                      );
                    },
                  },
                })}
                autoFocus
                type="text"
                placeholder="Enter ISO 8601 DateTime"
                onFocus={(evt) => evt.target.select()}
              />

              <InfoDiv
                type="error"
                className={cn("text-xs", { "invisible": !errors.stopTime })}
              >
                {errors.stopTime?.message?.length
                  ? errors.stopTime?.message
                  : "Invalid date string please use ISO8601 format, eg. 2021-01-01T00:00:00Z"}
              </InfoDiv>
            </div>
            <div className="text-left mx-auto flex flex-col w-full">
              <label className="label"># Steps</label>
              <SimpleSelect
                options={timeStepOptions}
                value={`${stepInterval}`}
                onValueChange={(value) => setStepInterval(Number(value))}
              />
            </div>
            {visibilityTrackingViewers.length > 0 && (
              <div className="text-left text-sm">
                <h3>Entities</h3>
                <VisibilityEntityList
                  trackingViewers={visibilityTrackingViewers}
                  removeItem={removeVisibilityTrackingViewer}
                />
              </div>
            )}
          </div>
        </div>
        <div className="text-left ml-4 w-[350px] my-4">
          <h3 className="flex items-center">
            <ExclamationTriangleIcon className="w-5 h-5 mr-2 inline-block text-yellow-200" />
            Note on Accuracy
          </h3>
          <div className="mt-1 space-y-2 text-xs italic">
            <p>
              This tool is suitable for quick illumination inspection. Due to 3D
              terrain simplification and browser constraints, results from this
              tool may not be entirely accurate.
            </p>
            <p>
              It is recommended to compare these results with other tools for
              validation, such as QTS.
            </p>
          </div>
        </div>
        <div className="flex flex-row">
          <div className="ml-auto">
            <Button type="submit">Compute!</Button>
          </div>
        </div>
      </form>
    </div>
  );

  return (
    <>
      <Dialog
        modal={false}
        open={isDialogOpen && !isProcessing}
        onOpenChange={(open) => {
          onOpenDialogChange(open);
        }}
      >
        <div className="flex flex-row">
          <Tooltip content={tooltipContent} wrap>
            <DialogTrigger asChild>
              <Button 
              {...buttonProps}
              isDisabled={isDisabled || !map || map.isDestroyed() || isProcessing}>
                <SunIcon size={14} className="mr-1" />
                Visibility Analysis
              </Button>
            </DialogTrigger>
          </Tooltip>
          {(!size || size !== "sm") && (
            <Tooltip content="Open Visibility Info" wrap>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={(event) => {
                  onLoadVisibilityInfo(event);
                  // Reset the input value
                  event.target.value = "";
                }}
                accept=".csv"
              />
              <Button 
                isDisabled={!map || map.isDestroyed() || isProcessing}
                variant="secondary"
                isQuiet
                onPress={() => fileInputRef.current?.click()}>
                <UploadIcon size={16} className="mt-1" />
              </Button>
            </Tooltip>)
          }
        </div>
        <VisuallyHidden>
          <DialogTitle>Visibility Analysis {map?.trackedEntity && (`Along Traverse${traverseLabel ? `: ${traverseLabel}` : ''}`)}</DialogTitle>
        </VisuallyHidden>
        <DialogContent
          className="max-w-none flex w-fit"
          onOpenAutoFocus={preventDefault}
          onCloseAutoFocus={preventDefault}
          onPointerDownOutside={preventDefault}
          onInteractOutside={preventDefault}
        >
          {isUpdateFinished && plotData ? (
            <div className="m-4 w-full">
              <VisibilityPlot
                plotTitle={plotData.title + (isTraverse ? (` (Traverse${traverseLabel ? `: ${traverseLabel}` : ''})`) : "")}
                csvData={plotData.csvData}
                nRows={plotData.nRows}
                eclipseData={plotData.eclipseData}
                trackingViewers={visibilityTrackingViewers}
                timeStep={niceformatIntervalTimeRound(progressInfo.timeStep)[1]}
                numSteps={progressInfo.numSteps}
                currentStep={progressInfo.currentStep}
                execTime={
                  progressInfo.execTime
                    ? niceformatIntervalTimeRound(progressInfo.execTime)[1]
                    : undefined
                }
                csvUrl={
                  isDefined(progressInfo.csvUrl)
                    ? progressInfo.csvUrl
                    : undefined
                }
              />
            </div>
          ) : (
            VisibilityForm
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function preventDefault(evt: Event) {
  evt.preventDefault();
}
